import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { formatString } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';

export const SimilarMindsVoting: React.FC = () => {
  const { socket, gameState, currentRoom, mySimilarMindsAnswerId, myPlayerId } = useSocket();
  const t = useTranslations();
  const sm = t.games.similarMinds;
  const [selected, setSelected] = React.useState<string[]>([]);
  const [now, setNow] = React.useState(() => Date.now());
  const autoSubmitAttemptedRef = React.useRef(false);

  const participants = currentRoom?.players.filter((p) => !p.isSpectator) ?? [];
  const participantMaxVotes = Math.max(1, participants.length - 1);
  const configuredMaxVotes = currentRoom?.similarMindsConfig?.votesPerPlayer ?? 2;
  const maxVotes = Math.max(1, Math.min(participantMaxVotes, configuredMaxVotes));

  const myVotes = myPlayerId ? gameState?.similarMindsVotes?.[myPlayerId] : undefined;
  const hasVoted = Array.isArray(myVotes) && myVotes.length >= 1;

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    if (currentRoom?.phase !== 'voting') {
      setSelected([]);
      autoSubmitAttemptedRef.current = false;
    }
  }, [currentRoom?.phase]);

  React.useEffect(() => {
    if (hasVoted && myVotes) {
      setSelected(myVotes);
    }
  }, [hasVoted, myVotes]);

  const timer = gameState?.timer;
  const timeRemaining =
    timer && timer.timerEndTime > 0
      ? Math.max(0, Math.ceil((timer.timerEndTime - now) / 1000))
      : null;
  const hasVotingTimer = timeRemaining !== null;

  React.useEffect(() => {
    if (!hasVotingTimer || hasVoted || autoSubmitAttemptedRef.current) return;
    if (timeRemaining !== 0) return;
    if (!socket || selected.length < 1) return;
    autoSubmitAttemptedRef.current = true;
    socket.emit('client:submit-similar-minds-votes', selected);
  }, [hasVotingTimer, timeRemaining, hasVoted, socket, selected]);

  if (!gameState || !currentRoom) {
    return null;
  }

  const answers = gameState.similarMindsPublicAnswers ?? [];
  const question = gameState.similarMindsQuestion;

  const votedCount = Object.keys(gameState.similarMindsVotes ?? {}).filter((id) => {
    const v = gameState.similarMindsVotes?.[id];
    return Array.isArray(v) && v.length >= 1;
  }).length;

  const toggleSelect = (answerId: string) => {
    if (hasVoted) return;
    if (mySimilarMindsAnswerId && answerId === mySimilarMindsAnswerId) return;
    setSelected((prev) => {
      if (prev.includes(answerId)) {
        return prev.filter((id) => id !== answerId);
      }
      if (prev.length >= maxVotes) {
        return maxVotes === 1 ? [answerId] : [...prev.slice(1), answerId];
      }
      return [...prev, answerId];
    });
  };

  const handleConfirm = () => {
    if (!socket || hasVoted || selected.length < 1) return;
    socket.emit('client:submit-similar-minds-votes', selected);
  };

  const votingInstruction =
    maxVotes === 1
      ? sm.votingInstructionOne
      : formatString(sm.votingInstruction, { total: String(maxVotes) });

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      {question && (
        <div className="mb-4 p-3 sm:p-4 rounded-xl border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
          <p className="text-xs sm:text-sm font-bold text-teal-700 mb-1">{sm.questionLabel}</p>
          <p className="text-base sm:text-lg text-gray-900 whitespace-pre-wrap break-words">{question}</p>
        </div>
      )}
      <div className="text-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
          {sm.votingTitle}
        </h2>
        <p className="text-sm sm:text-base text-gray-700 mt-2">{votingInstruction}</p>
        {hasVotingTimer && (
          <>
            <p className="text-lg sm:text-xl font-bold text-teal-600 mt-2">
              {formatString(sm.timeRemaining, {
                time: `${timeRemaining}s`,
              })}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">{sm.votingAutoSubmitHint}</p>
          </>
        )}
        <p className="text-sm font-semibold text-gray-600 mt-1">
          {formatString(sm.votesSelected, {
            count: String(selected.length),
            total: String(maxVotes),
          })}
        </p>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          {formatString(sm.submittedProgress, {
            submitted: String(votedCount),
            total: String(participants.length),
          })}
        </p>
      </div>

      <div className="flex flex-col gap-3 mb-4">
        {answers.map((item, index) => {
          const isOwn = mySimilarMindsAnswerId === item.id;
          const isSelected = selected.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              disabled={hasVoted || isOwn}
              onClick={() => toggleSelect(item.id)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                isOwn
                  ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                  : isSelected
                    ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-300'
                    : 'border-teal-200 bg-white hover:border-teal-400'
              } ${hasVoted ? 'cursor-default' : ''}`}
            >
              <span className="text-xs font-bold text-teal-600 block mb-1">#{index + 1}</span>
              <p className="text-sm sm:text-base text-gray-800 whitespace-pre-wrap break-words">{item.text}</p>
            </button>
          );
        })}
      </div>

      {hasVoted ? (
        <p className="text-center text-green-600 font-semibold">{sm.votesSubmitted}</p>
      ) : (
        <button
          onClick={handleConfirm}
          disabled={selected.length < 1}
          className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-xl font-bold disabled:from-gray-400 disabled:to-gray-400"
        >
          {sm.confirmVotes}
        </button>
      )}
    </div>
  );
};
